import * as angular from 'angular';
import './styling.scss';
import * as template from './template.html';

import { HippoApiService } from 'services/hippoApi';
import { IRootScopeService } from 'angular';
import { formatAsDollars } from 'common/source/utilities/strings';
import { CurrentUserService } from 'services/currentUser';

import * as _ from 'lodash';
import { getLongNumericAmericanDateString, getShortNumericAmericanDateString } from 'common/source/utilities/dates';
import { ModalService } from 'common/source/services/modal';
import { paymentOptions } from 'utilities/paymentsHelper';

const module = angular.module('escrow-pay-now', []);

interface EscrowPayNow extends angular.IScope {
    escrowForm: any;
}

const PAYMENT_STATUSES_TO_RECHARGE_UPON_CREDIT_CARD_UPDATE: Array<PaymentStatus> = [
    'due',
    'failed',
    'not_received',
    'outstanding'
];

const STRIPE_CARD_ERRORS_ELEMENT_SELECTOR = '#card-error';

const paymentFrequencyToDisplayMap = {
    annually: 'Annual',
    quarterly: 'Quarterly',
    monthly: 'Monthly',
    semi_annually: 'Semi Annual'
};

interface EscrowPayNowController extends angular.IStrictController {
    isLastFailedPaymentSelected: () => boolean;
    canOnlyPayLastFailedPaymentOption: boolean;
    policyInfo: CustomerPolicyInfo;
    cancel: () => void;
    payNow: () => void;
    isUpcomingSelected: () => void;
    isRemainingSelected: () => void;
    selectedPaymentAmount: string;
    showSpinner: boolean;
    hasFailedPay: boolean;
    frequency: string;
    paymentDueDate?: string;
    newPaymentDueDate?: string;
    nextUnpaidAmount: string;
    remainingAmount: string;
    shouldShowFailureMessage: boolean;
    successfulPaymentFlag: boolean;
    saveNewCreditCardAndPayNow: (result: stripe.TokenResponse | undefined) => void;
    setSuccessMessage: (successMessage: string, showSaveAsDefaultPaymentMethodQuestion: boolean) => void;
    closePayNow: () => void;
    isRemainingAmountSameAsNextUnpaidAmount: boolean;
}

module.component('escrowPayNow', {
    bindings: {
        policyInfo: '<',
        setSuccessMessage: '<',
        closePayNow: '<',
        canOnlyPayLastFailedPaymentOption: '<'
    },
    template: template,
    // tslint:disable-next-line:no-big-function
    controller: function(
        this: EscrowPayNowController,
        $scope: EscrowPayNow,
        $stateParams: PolicyStateParams,
        $rootScope: IRootScopeService,
        hippoApiService: HippoApiService,
        currentUserService: CurrentUserService,
        modalService: ModalService
    ) {
        this.$onInit = () => {
            this.successfulPaymentFlag = false;
            this.hasFailedPay = hasPastFailedPayment();
            updateFrequencyInfo();
        };

        $rootScope.$on('updatedCustomerPolicyInfos', () => {
            updateFrequencyInfo();
        });

        const setPaymentAmountOptions = () => {
            // hide frequency based next unpaid amount if it is the same as remaining
            if (this.nextUnpaidAmount === this.remainingAmount) {
                this.isRemainingAmountSameAsNextUnpaidAmount = true;
                this.selectedPaymentAmount = 'remaining';
            }
        };

        const updateFrequencyInfo = () => {
            const policyIndex = $stateParams.policyIndex;
            currentUserService
                .getCustomerPolicyInfoByIndex(policyIndex)
                .then((policyInfo) => {
                    this.nextUnpaidAmount = formatAsDollars(policyInfo.next_unpaid_payment_amount, 2);
                    this.paymentDueDate = policyInfo.next_unpaid_payment_due_date
                        ? getShortNumericAmericanDateString(policyInfo.next_unpaid_payment_due_date)
                        : '';

                    this.remainingAmount = formatAsDollars(policyInfo.remaining_payment_amount, 2);
                    this.frequency = paymentFrequencyToDisplayMap[policyInfo.checkout_data.payment_frequency];
                    this.hasFailedPay = hasPastFailedPayment(policyInfo);

                    setPaymentAmountOptions();

                    this.showSpinner = false;
                })
                .then((policyInfo) => {
                    if (this.successfulPaymentFlag) {
                        this.newPaymentDueDate = this.paymentDueDate;
                        showSuccessMessage();
                        modalService.closeAllDialogs();
                    }
                });
        };

        const hasPastFailedPayment = (policyInfo = this.policyInfo) =>
            _.includes(PAYMENT_STATUSES_TO_RECHARGE_UPON_CREDIT_CARD_UPDATE, policyInfo.payment_status);

        this.isUpcomingSelected = () => {
            return this.selectedPaymentAmount === paymentOptions.nextUnpaid;
        };

        this.isRemainingSelected = () => {
            return this.selectedPaymentAmount === paymentOptions.remaining;
        };

        this.isLastFailedPaymentSelected = () => {
            return this.selectedPaymentAmount === paymentOptions.lastFailedPayment;
        };

        const exitEditMode = () => {
            this.showSpinner = false;
            this.shouldShowFailureMessage = false;
            this.setSuccessMessage('', false);
        };

        this.cancel = () => {
            exitEditMode();
            this.closePayNow();
        };

        const updateCustomerPolicyInfo = () => {
            currentUserService.getUpdatedCustomerPolicyInfos().then((result) => {
                $rootScope.$emit('updatedCustomerPolicyInfos');
            });
        };

        this.payNow = () => {
            // hide failure messages when new pay now event is triggered
            this.shouldShowFailureMessage = false;
            setCreditCardInputErrorMessage('');

            this.showSpinner = true;

            $scope.$broadcast('stripeCardSave');
        };

        const showSuccessMessage = () => {
            this.setSuccessMessage(
                `Thank you for your payment!<br>Your default credit card information has been saved.`,
                true
            );
        };

        const showFailureMessage = () => {
            this.showSpinner = false;
            this.shouldShowFailureMessage = true;
            // clear credit card error message if its not a credit card info problem
            setCreditCardInputErrorMessage('');
            // clear success message
            this.setSuccessMessage('', false);
        };

        this.saveNewCreditCardAndPayNow = (result) => {
            // used when in edit mode and credit card info was changed
            // saves new credit card info then charges the selected amount
            if (result && result.error && result.error.message) {
                setCreditCardInputErrorMessage(result.error.message);
            } else if (result && result.token) {
                // clear credit card input error message while processing new credit card info
                setCreditCardInputErrorMessage('');
                this.showSpinner = true;

                saveStripeToken(result.token)
                    .then(() => {
                        payNowWithSavedCreditCard();
                    })
                    .catch(() => {
                        showFailureMessage();
                    });
            } else {
                showFailureMessage();
            }
        };

        const setCreditCardInputErrorMessage = (errorMessage: string) => {
            const cardError = document.querySelector(STRIPE_CARD_ERRORS_ELEMENT_SELECTOR);

            if (!cardError) {
                return;
            }

            cardError.textContent = errorMessage;
            this.showSpinner = false;
        };

        const saveStripeToken = (stripeToken: stripe.Token) => {
            const policyNumber = this.policyInfo.id;

            const policyInfoUpdate: CustomerPolicyInfoUpdate = {
                checkout_data: {
                    payment_token: stripeToken.id
                }
            };

            return hippoApiService.updateCustomerPolicy(policyNumber, policyInfoUpdate);
        };

        const payNowWithSavedCreditCard = () => {
            const policyNumber = this.policyInfo.id;
            const selected = {
                paymentOption: this.selectedPaymentAmount,
                paymentDueDate: getLongNumericAmericanDateString(this.paymentDueDate as AmericanDateString)
            };

            hippoApiService.updateFrequencyOption(policyNumber, selected).then(() => {
                // Final callback for all pay now paths
                // Sets successful payment flag to trigger show success message on update
                this.successfulPaymentFlag = true;
                updateCustomerPolicyInfo();
            }, showFailureMessage);
        };
    }
});

export { module };
